import React, { useState } from 'react';
import {
    Box,
    Container,
    Heading,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Button,
    VStack,
    HStack,
    Text,
    Input,
    FormControl,
    FormLabel,
    Textarea,
    useToast,
    Alert,
    AlertIcon,
    Divider,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Checkbox,
    RadioGroup,
    Radio,
    Stack
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { HiDocumentDownload, HiArrowLeft } from 'react-icons/hi';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const Forms = () => {
    const navigate = useNavigate();
    const toast = useToast();
    
    // Form data state
    const [formData, setFormData] = useState({
        // Advisory Note variables
        student_or_parent_name: '',
        signatory_id: '',
        ward_name: '',
        ward_id: '',
        advisory_date_ddmmyyyy: '',
        
        // Medical Insurance Opt Out variables
        letter_date_ddmmyyyy: '',
        student_name: '',
        nric_fin_passport: '',
        
        // Offer of Admission variables
        offer_date_ddmmyyyy: '',
        course_name: '',
        course_duration: '',
        class_timing_text: '',
        commencement_date_ddmmyyyy: '',
        completion_date_ddmmyyyy: '',
        venue_address: '',
        contact_person_name: '',
        contact_number: '',
        contact_email: '',
        registration_fee: '',
        course_fee: '',
        discount_amount: '',
        total_fees_payable: '',
        num_instalments: '',
        instalment_amount: '',
        instalment_start_ddmmyyyy: '',
        instalment_end_ddmmyyyy: '',
        total_course_fees: '',
        reply_date_ddmmyyyy: '',
        
        // Pre-Course Counselling Checklist variables
        student_phone: '',
        student_email: '',
        staff_name: '',
        staff_date_ddmmyyyy: '',
        student_date_ddmmyyyy: '',
        date_received_ddmmyyyy: '',
        received_by: '',
        briefing_quality: 'satisfactory',
        // Checklist items (b1-b34)
        b1: false, b2: false, b3: false, b4: false, b5: false, b6: false, b7: false, b8: false,
        b9: false, b10: false, b11: false, b12: false, b13: false, b14: false, b15: false, b16: false,
        b17: false, b18: false, b19: false, b20: false, b21: false, b22: false, b23: false, b24: false,
        b25: false, b26: false, b27: false, b28: false, b29: false, b30: false, b31: false, b32: false,
        b33: false, b34: false
    });

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const generatePDF = async (formType) => {
        try {
            const element = document.getElementById(`form-${formType}`);
            if (!element) {
                toast({
                    title: "Error",
                    description: "Form element not found",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                allowTaint: true
            });
            
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const imgWidth = 210;
            const pageHeight = 295;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;

            let position = 0;

            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft >= 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            pdf.save(`${formType}-form.pdf`);
            
            toast({
                title: "Success",
                description: "PDF generated successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast({
                title: "Error",
                description: "Failed to generate PDF",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const replaceVariables = (htmlContent) => {
        let content = htmlContent;
        Object.keys(formData).forEach(key => {
            const regex = new RegExp(`{{${key}}}`, 'g');
            content = content.replace(regex, formData[key] || '');
        });
        return content;
    };

    const handleFormInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const AdvisoryNoteForm = () => (
        <Box>
            {/* Form Preview */}
            <Box id="form-advisory" p={6} bg="white" borderRadius="lg" boxShadow="md" border="1px solid" borderColor="gray.200">
                <Heading size="md" textAlign="center" mb={4}>
                    Advisory Note to Students (Form 12)
                </Heading>
                
                <Box 
                    fontSize="sm" 
                    lineHeight="1.4"
                    fontFamily="serif"
                >
                    <div style={{textAlign:"center", marginBottom:"1rem"}}>
                        <div>Regulation 25(5)(b)</div>
                        <h3 style={{margin:"0.2rem 0"}}>FORM 12</h3>
                        <div>PRIVATE EDUCATION ACT (No. 21 of 2009)</div>
                        <div>PRIVATE EDUCATION REGULATIONS</div>
                        <h2 style={{marginTop:"0.6rem"}}>ADVISORY NOTE TO STUDENTS</h2>
                    </div>
                    
                    <p>This note is for a prospective student.</p>
                    <p>
                        You are strongly encouraged to thoroughly research on the private education institution (PEI)
                        conducting the course before signing up for any course. You should consider, for example,
                        the reputation of the PEI, the teacher–student ratio of its classes, the qualifications of the
                        teaching staff, and the course materials provided by the PEI.
                    </p>
                    <p>
                        By signing and returning the Student Contract (the "Contract"), you agree to the terms and conditions
                        which will bind you and the PEI, if you accept the PEI's offer of a place in a course of study offered
                        or provided by the PEI.
                    </p>
                    <p>
                        You should ask the PEI to allow you to read a copy of the Contract (with all blanks filled in and options selected)
                        in both English and the official language of your home country, if necessary. For your own protection, you should
                        review all the PEI's policies, and check carefully that you agree to all the terms of the Contract, including the
                        details relating to each of the following sections, before signing the Contract:
                    </p>
                    <ol type="a" style={{paddingLeft:"1.2rem"}}>
                        <li>The duration of the course, including holidays and examination schedules, and contact hours by days and week;</li>
                        <li>The total fees payable, including course fees and other related costs;</li>
                        <li>Dates when respective payments are due;</li>
                        <li>The refund policy in the event of voluntary withdrawal (by you) or enforced dismissal from the course or programme (by PEI);</li>
                        <li>The Fee Protection Scheme you are subscribed to and its coverage;</li>
                        <li>The dispute resolution methods available; and</li>
                        <li>Information about the PEI's policies on academic and disciplinary matters;</li>
                        <li>The degree or diploma or qualification which will be awarded to you upon successful completion of the course.</li>
                    </ol>
                    <p>
                        If you have any doubt about the contents of the Contract, or if the terms are different from what the agent or
                        the PEI have informed you previously, or advertised, you should always seek advice and/or clarifications before
                        signing the Contract.
                    </p>
                    
                    <hr style={{margin:"1.2rem 0"}} />
                    
                    <fieldset style={{border:"1px solid #000", padding:"1rem"}}>
                        <legend>This portion is to be completed by the signatory of the Student Contract</legend>
                        <p style={{margin:"0.4rem 0"}}>
                            I,
                            <label>
                                (name of student/parent/guardian)
                                <input 
                                    type="text" 
                                    name="student_or_parent_name" 
                                    value={formData.student_or_parent_name || ''} 
                                    onChange={handleFormInputChange}
                                    style={{width:"22rem", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                                />
                            </label>
                            , NRIC/FIN No./Passport No.
                            <label>
                                <input 
                                    type="text" 
                                    name="signatory_id" 
                                    value={formData.signatory_id || ''} 
                                    onChange={handleFormInputChange}
                                    style={{width:"14rem", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                                />
                            </label>
                        </p>
                        
                        <p style={{margin:"0.4rem 0"}}>
                            have read and understood this advisory note before signing the Student Contract for myself/ my ward**.
                        </p>
                        
                        <p style={{margin:"0.4rem 0"}}>
                            (name of ward, if student is below 18)
                            <label>
                                <input 
                                    type="text" 
                                    name="ward_name" 
                                    value={formData.ward_name || ''} 
                                    onChange={handleFormInputChange}
                                    style={{width:"20rem", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                                />
                            </label>
                            &nbsp;&nbsp; (NRIC/Passport)
                            <label>
                                <input 
                                    type="text" 
                                    name="ward_id" 
                                    value={formData.ward_id || ''} 
                                    onChange={handleFormInputChange}
                                    style={{width:"12rem", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                                />
                            </label>
                        </p>
                        
                        <p style={{margin:"0.4rem 0"}}>
                            with <strong>Edusphere College of Management and Technology Pte Ltd</strong> (name of PEI).
                        </p>
                        
                        <div style={{display:"flex", alignItems:"center", gap:"1rem", marginTop:"0.6rem"}}>
                            <div style={{flex:1}}>
                                <label>Signature of student or parent/guardian</label>
                                <div style={{borderBottom:"1px solid #000", height:"2rem"}}></div>
                            </div>
                            <div>
                                <label>Date (DD-MM-YYYY)</label><br />
                                <input 
                                    type="text" 
                                    name="advisory_date_ddmmyyyy" 
                                    value={formData.advisory_date_ddmmyyyy || ''} 
                                    onChange={handleFormInputChange}
                                    style={{border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                                />
                            </div>
                        </div>
                        <p style={{fontSize:"0.85rem", marginTop:"0.6rem"}}>** Delete where inapplicable.</p>
                    </fieldset>
                </Box>
                
                <Button
                    colorScheme="blue"
                    leftIcon={<HiDocumentDownload />}
                    onClick={() => generatePDF('advisory')}
                    mt={4}
                >
                    Generate PDF
                </Button>
            </Box>
        </Box>
    );

    const MedicalInsuranceForm = () => (
        <Box>
            {/* Form Preview */}
            <Box id="form-medical" p={6} bg="white" borderRadius="lg" boxShadow="md" border="1px solid" borderColor="gray.200">
                <Heading size="md" textAlign="center" mb={4}>
                    Medical Insurance Opt Out Form
                </Heading>
                
                <Box 
                    fontSize="sm" 
                    lineHeight="1.4"
                    fontFamily="serif"
                >
                    <header style={{textAlign:"center"}}>
                        <h2 style={{marginBottom:"0.2rem"}}>MEDICAL INSURANCE OPT OUT FORM</h2>
                    </header>
                    
                    <p>
                        <strong>DATE:</strong>
                        <input 
                            type="text" 
                            name="letter_date_ddmmyyyy" 
                            value={formData.letter_date_ddmmyyyy || ''} 
                            onChange={handleFormInputChange}
                            style={{border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                        />
                    </p>
                    
                    <p><strong>From:</strong></p>
                    <p>
                        Name:
                        <input 
                            type="text" 
                            name="student_name" 
                            value={formData.student_name || ''} 
                            onChange={handleFormInputChange}
                            style={{width:"24rem", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                        />
                        &nbsp;&nbsp; NRIC/FIN No./Passport No.:
                        <input 
                            type="text" 
                            name="nric_fin_passport" 
                            value={formData.nric_fin_passport || ''} 
                            onChange={handleFormInputChange}
                            style={{width:"14rem", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                        />
                    </p>
                    
                    <p><strong>To:</strong><br />
                        The Manager,<br />
                        Edusphere College of Management and Technology Pte Ltd<br />
                        CPE Reg No: 201940174R<br />
                        7500A Beach Rd, #05-312 THE PLAZA, Singapore 199591
                    </p>
                    
                    <p><em>SUB: Declaration of Medical Insurance</em></p>
                    
                    <p>
                        I hereby declare that I am responsible for my own medical insurance; kindly opt me out from the medical insurance.
                    </p>
                    
                    <p>Yours sincerely,</p>
                    
                    <div style={{marginTop:"1rem"}}>
                        <div style={{borderBottom:"1px solid #000", height:"2rem", width:"18rem"}}></div>
                        <div>
                            Student Name:
                            <input 
                                type="text" 
                                name="student_name" 
                                value={formData.student_name || ''} 
                                onChange={handleFormInputChange}
                                style={{width:"22rem", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                            />
                            &nbsp;&nbsp; NRIC/FIN No./Passport No.:
                            <input 
                                type="text" 
                                name="nric_fin_passport" 
                                value={formData.nric_fin_passport || ''} 
                                onChange={handleFormInputChange}
                                style={{width:"14rem", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                            />
                        </div>
                    </div>
                </Box>
                
                <Button
                    colorScheme="blue"
                    leftIcon={<HiDocumentDownload />}
                    onClick={() => generatePDF('medical')}
                    mt={4}
                >
                    Generate PDF
                </Button>
            </Box>
        </Box>
    );

    const OfferOfAdmissionForm = () => (
        <Box>
            {/* Form Preview */}
            <Box id="form-offer" p={6} bg="white" borderRadius="lg" boxShadow="md" border="1px solid" borderColor="gray.200">
                <Heading size="md" textAlign="center" mb={4}>
                    Offer of Admission
                </Heading>
                
                <Box 
                    fontSize="sm" 
                    lineHeight="1.4"
                    fontFamily="serif"
                >
                    <p><strong>Date:</strong> 
                        <input 
                            type="text" 
                            name="offer_date_ddmmyyyy" 
                            value={formData.offer_date_ddmmyyyy || ''} 
                            onChange={handleFormInputChange}
                            style={{border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                        />
                    </p>
                    
                    <p>
                        Dear
                        <input 
                            type="text" 
                            name="student_name" 
                            value={formData.student_name || ''} 
                            onChange={handleFormInputChange}
                            style={{width:"24rem", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                        />,<br />
                        NRIC/FIN No./Passport No.:
                        <input 
                            type="text" 
                            name="nric_fin_passport" 
                            value={formData.nric_fin_passport || ''} 
                            onChange={handleFormInputChange}
                            style={{width:"14rem", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                        />
                    </p>
                    
                    <header style={{textAlign:"center"}}>
                        <h2 style={{marginBottom:"0.2rem"}}>OFFER OF ADMISSION</h2>
                    </header>
                    
                    <p>
                        We are pleased to inform you that you have been offered a place to study at
                        <strong>EDUSPHERE COLLEGE OF MANAGEMENT AND TECHNOLOGY PTE LTD</strong>. The details of the offer are as follows:
                    </p>
                    
                    <table role="grid" style={{borderCollapse:"collapse", width:"100%", margin:"0.6rem 0"}}>
                        <tr>
                            <td style={{border:"1px solid #000", padding:"0.4rem", width:"32%"}}>Course Name</td>
                            <td style={{border:"1px solid #000", padding:"0.4rem"}}>
                                <input 
                                    type="text" 
                                    name="course_name" 
                                    value={formData.course_name || ''} 
                                    onChange={handleFormInputChange}
                                    style={{width:"100%", border:"none", background:"transparent"}}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td style={{border:"1px solid #000", padding:"0.4rem"}}>Course Duration</td>
                            <td style={{border:"1px solid #000", padding:"0.4rem"}}>
                                <input 
                                    type="text" 
                                    name="course_duration" 
                                    value={formData.course_duration || ''} 
                                    onChange={handleFormInputChange}
                                    style={{width:"100%", border:"none", background:"transparent"}}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td style={{border:"1px solid #000", padding:"0.4rem"}}>Class Timing</td>
                            <td style={{border:"1px solid #000", padding:"0.4rem"}}>
                                <input 
                                    type="text" 
                                    name="class_timing_text" 
                                    value={formData.class_timing_text || ''} 
                                    onChange={handleFormInputChange}
                                    style={{width:"100%", border:"none", background:"transparent"}}
                                    placeholder="e.g. Refer to Appendix 3 – Timetable"
                                />
                            </td>
                        </tr>
                        <tr>
                            <td style={{border:"1px solid #000", padding:"0.4rem"}}>Commencement Date</td>
                            <td style={{border:"1px solid #000", padding:"0.4rem"}}>
                                <input 
                                    type="text" 
                                    name="commencement_date_ddmmyyyy" 
                                    value={formData.commencement_date_ddmmyyyy || ''} 
                                    onChange={handleFormInputChange}
                                    style={{border:"none", background:"transparent"}}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td style={{border:"1px solid #000", padding:"0.4rem"}}>Expected Date of Completion</td>
                            <td style={{border:"1px solid #000", padding:"0.4rem"}}>
                                <input 
                                    type="text" 
                                    name="completion_date_ddmmyyyy" 
                                    value={formData.completion_date_ddmmyyyy || ''} 
                                    onChange={handleFormInputChange}
                                    style={{border:"none", background:"transparent"}}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td style={{border:"1px solid #000", padding:"0.4rem"}}>Venue</td>
                            <td style={{border:"1px solid #000", padding:"0.4rem"}}>
                                <input 
                                    type="text" 
                                    name="venue_address" 
                                    value={formData.venue_address || ''} 
                                    onChange={handleFormInputChange}
                                    style={{width:"100%", border:"none", background:"transparent"}}
                                    placeholder="7500A Beach Rd, #05-312 THE PLAZA, Singapore 199591"
                                />
                            </td>
                        </tr>
                    </table>
                    
                    <h3>Key Information:</h3>
                    <ul>
                        <li>Classes and assessment will be conducted Face-to-Face.</li>
                        <li>Information on the course fees, payment schedule and refund policy can be found in this offer letter.</li>
                    </ul>
                    
                    <h3>Attendance Requirements:</h3>
                    <ul>
                        <li>You are required to attain at least 75% class attendance for every module.</li>
                        <li>Absence due to medical/hospitalisation leave, reservist training or overseas assignments must be kept informed to us at all times.</li>
                    </ul>
                    
                    <h3>Examination Requirements</h3>
                    <p><strong>Eligibility for Assessment / Examination</strong></p>
                    <ul>
                        <li>Achieve at least 75% class attendance for every module;</li>
                        <li>Learners will be barred from the assessment if their attendance falls below 75%. He/She will need to re-module that particular module in the next available class.</li>
                    </ul>
                    
                    <h3>Acceptance of Offer:</h3>
                    <p>
                        To accept this offer, please duly complete, sign and mail the following document to Edusphere College:
                    </p>
                    <ul>
                        <li>Reply Slip (Appendix 2)</li>
                    </ul>
                    
                    <p>
                        Should you have any query with regards to your programme, please do not hesitate to contact
                        <input 
                            type="text" 
                            name="contact_person_name" 
                            value={formData.contact_person_name || ''} 
                            onChange={handleFormInputChange}
                            style={{width:"16rem", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                            placeholder="Samsudeen Mohamed Tahir"
                        /> at
                        <input 
                            type="text" 
                            name="contact_number" 
                            value={formData.contact_number || ''} 
                            onChange={handleFormInputChange}
                            style={{width:"12rem", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                            placeholder="+65 8220 0095"
                        />
                        or email to 
                        <input 
                            type="text" 
                            name="contact_email" 
                            value={formData.contact_email || ''} 
                            onChange={handleFormInputChange}
                            style={{width:"18rem", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                            placeholder="info@edusphere.edu.sg"
                        />
                    </p>
                    
                    <p>Yours sincerely,</p>
                    <p>
                        <strong>SAMSUDEEN MOHAMED TAHIR (Mr)</strong><br />
                        Edusphere College of Management and Technology Pte Ltd<br />
                        7500A Beach Rd, #05-312 THE PLAZA, Singapore 199591
                    </p>
                    
                    <hr style={{margin:"1.2rem 0"}} />
                    
                    <h3>Appendix 1 — Payment Schedule</h3>
                    
                    <h4>COURSE FEES</h4>
                    <table role="grid" style={{borderCollapse:"collapse", width:"100%", margin:"0.4rem 0"}}>
                        <thead>
                            <tr>
                                <th style={{border:"1px solid #000", padding:"0.4rem", textAlign:"left"}}>Fees Breakdown (shows the full breakdown of total payable course fees)</th>
                                <th style={{border:"1px solid #000", padding:"0.4rem", textAlign:"left"}}>Total Payable (with GST, if any) (S$)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>Registration fee (Non-Refundable)</td>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>
                                    <input 
                                        type="text" 
                                        name="registration_fee" 
                                        value={formData.registration_fee || ''} 
                                        onChange={handleFormInputChange}
                                        style={{border:"none", background:"transparent"}}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>Course Fee</td>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>
                                    <input 
                                        type="text" 
                                        name="course_fee" 
                                        value={formData.course_fee || ''} 
                                        onChange={handleFormInputChange}
                                        style={{border:"none", background:"transparent"}}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>Discount</td>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>
                                    <input 
                                        type="text" 
                                        name="discount_amount" 
                                        value={formData.discount_amount || ''} 
                                        onChange={handleFormInputChange}
                                        style={{border:"none", background:"transparent"}}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}><strong>Total Fees Payable (After Discount)</strong></td>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>
                                    <input 
                                        type="text" 
                                        name="total_fees_payable" 
                                        value={formData.total_fees_payable || ''} 
                                        onChange={handleFormInputChange}
                                        style={{border:"none", background:"transparent"}}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    
                    <h4>INSTALMENT SCHEDULE</h4>
                    <table role="grid" style={{borderCollapse:"collapse", width:"100%", margin:"0.4rem 0"}}>
                        <tbody>
                            <tr>
                                <td style={{border:"1px solid #000", padding:"0.4rem", width:"40%"}}>No of Instalments</td>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>
                                    <input 
                                        type="text" 
                                        name="num_instalments" 
                                        value={formData.num_instalments || ''} 
                                        onChange={handleFormInputChange}
                                        style={{border:"none", background:"transparent"}}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>Instalment Amount (Per Month)</td>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>
                                    <input 
                                        type="text" 
                                        name="instalment_amount" 
                                        value={formData.instalment_amount || ''} 
                                        onChange={handleFormInputChange}
                                        style={{border:"none", background:"transparent"}}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>Instalment Start date</td>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>
                                    <input 
                                        type="text" 
                                        name="instalment_start_ddmmyyyy" 
                                        value={formData.instalment_start_ddmmyyyy || ''} 
                                        onChange={handleFormInputChange}
                                        style={{border:"none", background:"transparent"}}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>Instalment End date</td>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>
                                    <input 
                                        type="text" 
                                        name="instalment_end_ddmmyyyy" 
                                        value={formData.instalment_end_ddmmyyyy || ''} 
                                        onChange={handleFormInputChange}
                                        style={{border:"none", background:"transparent"}}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}><strong>Total Course Fees Payable</strong></td>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>
                                    <input 
                                        type="text" 
                                        name="total_course_fees" 
                                        value={formData.total_course_fees || ''} 
                                        onChange={handleFormInputChange}
                                        style={{border:"none", background:"transparent"}}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    
                    <h4>REFUND POLICY</h4>
                    <table role="grid" style={{borderCollapse:"collapse", width:"100%", margin:"0.4rem 0"}}>
                        <thead>
                            <tr>
                                <th style={{border:"1px solid #000", padding:"0.4rem", textAlign:"left"}}>% of the amount of fees paid under Schedules B and C</th>
                                <th style={{border:"1px solid #000", padding:"0.4rem", textAlign:"left"}}>If Student's written notice of withdrawal is received:</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>100% ("Maximum Refund")</td>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>More than 30 days before the Course Commencement Date</td>
                            </tr>
                            <tr>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>50%</td>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>Before, but not more than 30 days before the Course Commencement Date</td>
                            </tr>
                            <tr>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>25%</td>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>After, but not more than 15 days after the Course Commencement Date</td>
                            </tr>
                            <tr>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>0%</td>
                                <td style={{border:"1px solid #000", padding:"0.4rem"}}>More than 15 days after the Course Commencement Date</td>
                            </tr>
                        </tbody>
                    </table>
                    
                    <hr style={{margin:"1.2rem 0"}} />
                    
                    <h3>Appendix 2 — Reply Slip</h3>
                    <p><strong>TO:</strong> EDUSPHERE COLLEGE OF MANAGEMENT AND TECHNOLOGY PTE LTD<br />
                        7500A Beach Rd, #05-312 THE PLAZA, Singapore 199591.
                    </p>
                    
                    <h4>REPLY SLIP</h4>
                    <p>
                        I, (Name of Applicant)
                        <input 
                            type="text" 
                            name="student_name" 
                            value={formData.student_name || ''} 
                            onChange={handleFormInputChange}
                            style={{width:"24rem", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                        />,
                        NRIC/FIN No./Passport No.:
                        <input 
                            type="text" 
                            name="nric_fin_passport" 
                            value={formData.nric_fin_passport || ''} 
                            onChange={handleFormInputChange}
                            style={{width:"14rem", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                        />
                        accept offer of a place in the above-said course at Edusphere College of Management and Technology Pte Ltd.
                    </p>
                    
                    <p>
                        I confirm that I understand the information on the course fees, payment schedule and refund policy in this offer letter.
                    </p>
                    
                    <div style={{display:"flex", alignItems:"flex-end", gap:"2rem", marginTop:"1rem"}}>
                        <div style={{flex:1}}>
                            <div style={{borderBottom:"1px solid #000", height:"2rem"}}></div>
                            <div>(Signature of Applicant)</div>
                        </div>
                        <div>
                            Date (DD/MM/YYYY): 
                            <input 
                                type="text" 
                                name="reply_date_ddmmyyyy" 
                                value={formData.reply_date_ddmmyyyy || ''} 
                                onChange={handleFormInputChange}
                                style={{border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                            />
                        </div>
                    </div>
                </Box>
                
                <Button
                    colorScheme="blue"
                    leftIcon={<HiDocumentDownload />}
                    onClick={() => generatePDF('offer')}
                    mt={4}
                >
                    Generate PDF
                </Button>
            </Box>
        </Box>
    );

    const CounsellingChecklistForm = () => (
        <Box>
            {/* Form Preview */}
            <Box id="form-counselling" p={6} bg="white" borderRadius="lg" boxShadow="md" border="1px solid" borderColor="gray.200">
                <Heading size="md" textAlign="center" mb={4}>
                    Pre-Course Counselling Checklist
                </Heading>
                
                <Box 
                    fontSize="sm" 
                    lineHeight="1.4"
                    fontFamily="serif"
                >
                    <header style={{textAlign:"center"}}>
                        <h2 style={{marginBottom:"0.2rem"}}>Pre-Course Counselling Checklist</h2>
                    </header>
                    
                    <h3>Instructions</h3>
                    <ol>
                        <li>Staff to brief and assist students/parents in accordance to this checklist at the point of enquiry and submit together with course application form to Edusphere College of Management and Technology.</li>
                        <li>Edusphere College of Management and Technology Manager will review information with student at point of admission and close any information gaps or doubts prior to signing of student contract.</li>
                        <li>Edusphere College of Management and Technology will provide information at Orientation and close any information gaps, clarifying any doubts, if necessary.</li>
                        <li>Staff to tick the "Checked" box accordingly.<br /><em>Note: All information provided is treated with the strictest confidentiality and is meant for internal use only.</em></li>
                    </ol>
                    
                    <fieldset style={{border:"1px solid #000", padding:"1rem"}}>
                        <legend>SECTION A: APPLICANT'S PARTICULARS</legend>
                        <p>
                            Name of Applicant (as in NRIC/FIN/Passport):
                            <input 
                                type="text" 
                                name="student_name" 
                                value={formData.student_name || ''} 
                                onChange={handleFormInputChange}
                                style={{width:"28rem", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                            />
                        </p>
                        <p>
                            NRIC/FIN No./Passport No.:
                            <input 
                                type="text" 
                                name="nric_fin_passport" 
                                value={formData.nric_fin_passport || ''} 
                                onChange={handleFormInputChange}
                                style={{width:"16rem", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                            />
                        </p>
                        <p>
                            Mobile / Home Tel No.:
                            <input 
                                type="text" 
                                name="student_phone" 
                                value={formData.student_phone || ''} 
                                onChange={handleFormInputChange}
                                style={{width:"16rem", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                            />
                        </p>
                        <p>
                            Email Address:
                            <input 
                                type="text" 
                                name="student_email" 
                                value={formData.student_email || ''} 
                                onChange={handleFormInputChange}
                                style={{width:"26rem", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                            />
                        </p>
                    </fieldset>
                    
                    <fieldset style={{border:"1px solid #000", padding:"1rem", marginTop:"1rem"}}>
                        <legend>SECTION B: CHECKLIST (Explanation with the aid of website: www.edusphere.edu.sg)</legend>
                        <table role="grid" style={{borderCollapse:"collapse", width:"100%"}}>
                            <thead>
                                <tr>
                                    <th style={{border:"1px solid #000", padding:"0.4rem", textAlign:"left", width:"6%"}}>No.</th>
                                    <th style={{border:"1px solid #000", padding:"0.4rem", textAlign:"left"}}>Items</th>
                                    <th style={{border:"1px solid #000", padding:"0.4rem", textAlign:"center", width:"12%"}}>Checked</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    "Brief school history, Vision, Mission, Values, Culture",
                                    "School location, photograph or actual physical school premises",
                                    "School physical facilities available",
                                    "Size and number of classrooms",
                                    "Organisation chart showing management team",
                                    "Members of academic and examination boards",
                                    "Teacher–student ratio",
                                    "Reference to CPE website [www.ssg.gov.sg/cpe]",
                                    "Course pre-requisites, requirements and any exemptions",
                                    "Suitability of student to course",
                                    "Opportunities for further education or job prospects after graduation",
                                    "Course modules & outlines",
                                    "Course duration and assessment schedules",
                                    "Certification awarded, award criteria & appeals",
                                    "Attendance requirements",
                                    "Application requirements & procedures",
                                    "Payment methods, channels and receipts",
                                    "Total payable fee throughout course duration, miscellaneous fees",
                                    "LONPAC Fee Protection Scheme / Industry-Wide Course Fee Protection Scheme",
                                    "Medical Insurance Scheme",
                                    "Late Payment & Refund Policy",
                                    "Commitment to avoidance of over- or under-charging",
                                    "Transfer/Withdrawal/Deferment Policies",
                                    "PEI Student Contract",
                                    "CPE Student Advisory Note",
                                    "Orientation Programme",
                                    "Student grievance procedure / Dispute resolution system",
                                    "Student support services available",
                                    "Feedback mechanism",
                                    "Service Quality Targets",
                                    "Confidentiality of student data",
                                    "Quality assurance of delivery of course",
                                    "Student Handbooks",
                                    "Edusphere College website [http://www.edusphere.edu.sg]"
                                ].map((item, index) => (
                                    <tr key={index}>
                                        <td style={{border:"1px solid #000", padding:"0.4rem"}}>{index + 1}</td>
                                        <td style={{border:"1px solid #000", padding:"0.4rem"}}>{item}</td>
                                        <td style={{border:"1px solid #000", textAlign:"center"}}>
                                            <input 
                                                type="checkbox" 
                                                name={`b${index + 1}`}
                                                checked={formData[`b${index + 1}`] || false}
                                                onChange={handleFormInputChange}
                                                style={{transform:"scale(1.2)"}}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </fieldset>
                    
                    <fieldset style={{border:"1px solid #000", padding:"1rem", marginTop:"1rem"}}>
                        <legend>SECTION C: DECLARATION (To be completed at the end of counselling)</legend>
                        <p><strong>Staff/Agent:</strong> I confirm that the above have been explained to the student.</p>
                        <p><strong>Student:</strong> I understand fully what has been communicated to me and I hereby acknowledge that I have been briefed
                            <label style={{marginLeft:"0.6rem"}}>
                                <input 
                                    type="radio" 
                                    name="briefing_quality" 
                                    value="satisfactory"
                                    checked={formData.briefing_quality === 'satisfactory'}
                                    onChange={handleFormInputChange}
                                    style={{marginRight:"0.3rem"}}
                                />
                                satisfactory
                            </label>
                            <label style={{marginLeft:"1rem"}}>
                                <input 
                                    type="radio" 
                                    name="briefing_quality" 
                                    value="not_satisfactory"
                                    checked={formData.briefing_quality === 'not_satisfactory'}
                                    onChange={handleFormInputChange}
                                    style={{marginRight:"0.3rem"}}
                                />
                                not satisfactory
                            </label>
                            on the above.
                        </p>
                        
                        <div style={{display:"flex", gap:"2rem", marginTop:"1rem"}}>
                            <div style={{flex:1}}>
                                <div>Name of Staff</div>
                                <input 
                                    type="text" 
                                    name="staff_name" 
                                    value={formData.staff_name || ''} 
                                    onChange={handleFormInputChange}
                                    style={{width:"100%", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                                /><br /><br />
                                <div>Signature of Staff &amp; Date</div>
                                <div style={{borderBottom:"1px solid #000", height:"2rem"}}></div>
                                <div style={{marginTop:"0.4rem"}}>Date (DD-MM-YYYY): 
                                    <input 
                                        type="text" 
                                        name="staff_date_ddmmyyyy" 
                                        value={formData.staff_date_ddmmyyyy || ''} 
                                        onChange={handleFormInputChange}
                                        style={{border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                                    />
                                </div>
                            </div>
                            <div style={{flex:1}}>
                                <div>Name of Student</div>
                                <input 
                                    type="text" 
                                    name="student_name" 
                                    value={formData.student_name || ''} 
                                    onChange={handleFormInputChange}
                                    style={{width:"100%", border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                                /><br /><br />
                                <div>Signature of Student &amp; Date</div>
                                <div style={{borderBottom:"1px solid #000", height:"2rem"}}></div>
                                <div style={{marginTop:"0.4rem"}}>Date (DD-MM-YYYY): 
                                    <input 
                                        type="text" 
                                        name="student_date_ddmmyyyy" 
                                        value={formData.student_date_ddmmyyyy || ''} 
                                        onChange={handleFormInputChange}
                                        style={{border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                                    />
                                </div>
                            </div>
                        </div>
                        
                        <div style={{marginTop:"1rem"}}>
                            <strong>FOR OFFICIAL USE ONLY</strong><br />
                            Date Received: 
                            <input 
                                type="text" 
                                name="date_received_ddmmyyyy" 
                                value={formData.date_received_ddmmyyyy || ''} 
                                onChange={handleFormInputChange}
                                style={{border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                            />
                            &nbsp;&nbsp; Received by Programme Manager: 
                            <input 
                                type="text" 
                                name="received_by" 
                                value={formData.received_by || ''} 
                                onChange={handleFormInputChange}
                                style={{border:"none", borderBottom:"1px solid #000", background:"transparent"}}
                            />
                        </div>
                    </fieldset>
                </Box>
                
                <Button
                    colorScheme="blue"
                    leftIcon={<HiDocumentDownload />}
                    onClick={() => generatePDF('counselling')}
                    mt={4}
                >
                    Generate PDF
                </Button>
            </Box>
        </Box>
    );

    return (
        <Container maxW="container.xl" py={8}>
            <VStack spacing={6} align="stretch">
                <HStack justify="space-between" align="center">
                    <Button
                        leftIcon={<HiArrowLeft />}
                        variant="outline"
                        onClick={() => navigate('/')}
                    >
                        Back to Home
                    </Button>
                    <Heading size="lg">Forms Management</Heading>
                    <Box w="120px" /> {/* Spacer for alignment */}
                </HStack>

                <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    Fill in the form fields below and click "Generate PDF" to download the completed form.
                </Alert>

                <Tabs variant="enclosed" colorScheme="blue">
                    <TabList>
                        <Tab>Advisory Note</Tab>
                        <Tab>Medical Insurance</Tab>
                        <Tab>Offer of Admission</Tab>
                        <Tab>Counselling Checklist</Tab>
                    </TabList>

                    <TabPanels>
                        <TabPanel px={0}>
                            <AdvisoryNoteForm />
                        </TabPanel>
                        <TabPanel px={0}>
                            <MedicalInsuranceForm />
                        </TabPanel>
                        <TabPanel px={0}>
                            <OfferOfAdmissionForm />
                        </TabPanel>
                        <TabPanel px={0}>
                            <CounsellingChecklistForm />
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </VStack>
        </Container>
    );
};

export default Forms;
